/**
 * Ejecuciones de modelo (`/api/ejecuciones`).
 *
 * ⚠️ Nombre LEGACY: la entidad se llama `EjecucionModelo` desde 2026-07 y las
 * rutas son `/api/ejecuciones`.
 *
 * **La ejecución es la ÚNICA que conoce el área evaluada**: `ambito` e
 * `id_programa` son columnas suyas y de ninguna otra tabla. Ni el modelo ni la
 * versión las tienen, así que cualquier dato que dependa del área debe partir de
 * aquí.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import type { Proceso, LiderFactor } from '@/interfaces/ProcesoAutoevaluacion'
import { http } from '@/services/http'

const BASE = '/api/ejecuciones'

/** Tipo de PROCESO: acreditación, reacreditación, renovación de registro. */
export interface TipoEjecucionModelo {
  id_tipo_ejecucion_modelo: number
  tipo: string
  descripcion?: string | null
  orden?: number
}

/** Catálogo para el selector del formulario de creación. */
export async function getTiposEjecucionModeloService(): Promise<TipoEjecucionModelo[]> {
  const result = await http.get<TipoEjecucionModelo[]>('/api/tipos-ejecucion-modelo')
  return result.ok ? (result.data ?? []) : []
}

/**
 * Consulta proceso autoevaluacion · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns Proceso[]
 */
export async function getProcesosService(params?: {
  id_programa?: number
  id_version?: number
  estado?: string
  periodo?: string
}): Promise<Proceso[]> {
  const query = params ? '?' + new URLSearchParams(params as any).toString() : ''
  const result = await http.get<any>(`${BASE}${query}`)
  if (!result.ok) return []
  const d = result.data
  const items: any[] = Array.isArray(d) ? d : d?.data ?? []
  return items.map(p => ({ ...p, id: p.id ?? p.id_ejecucion }) as Proceso)
}

/**
 * Consulta proceso autoevaluacion · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Proceso | null
 */
export async function getProcesoByIdService(id: number): Promise<Proceso | null> {
  const result = await http.get<Proceso>(`${BASE}/${id}`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Crea proceso autoevaluacion · petición `POST`.
 *
 * @returns { ok: boolean; data?: Proceso; errors?: Record<string, string[]> }
 */
export async function crearProcesoService(
  payload: Omit<Proceso, 'id' | 'estado' | 'created_at' | 'updated_at' | 'area' | 'version' | 'lideresFactor'>
): Promise<{ ok: boolean; data?: Proceso; errors?: Record<string, string[]> }> {
  const result = await http.post<any>(BASE, payload)
  if (!result.ok) return { ok: false, errors: result.errors }
  const d = result.data
  return { ok: true, data: d ? { ...d, id: d.id ?? d.id_ejecucion } as Proceso : undefined }
}

/**
 * Actualiza proceso autoevaluacion · petición `PUT`.
 *
 * @returns { ok: boolean; data?: Proceso; errors?: Record<string, string[]> }
 */
export async function updateProcesoService(
  id: number,
  payload: Partial<Omit<Proceso, 'id' | 'estado' | 'created_at' | 'updated_at' | 'area' | 'version' | 'lideresFactor'>>
): Promise<{ ok: boolean; data?: Proceso; errors?: Record<string, string[]> }> {
  const result = await http.put<Proceso>(`${BASE}/${id}`, payload)
  if (!result.ok) return { ok: false, errors: result.errors }
  return { ok: true, data: result.data }
}

/**
 * Cambia proceso autoevaluacion · petición `PATCH`.
 *
 * @returns { ok: boolean; data?: Proceso; errors?: Record<string, string[]> }
 */
export async function cambiarEstadoProcesoService(
  id: number,
  estado: Proceso['estado']
): Promise<{ ok: boolean; data?: Proceso; errors?: Record<string, string[]> }> {
  const result = await http.patch<any>(`${BASE}/${id}/estado`, { estado })
  if (!result.ok) return { ok: false, errors: result.errors }
  const d = result.data
  return { ok: true, data: d ? { ...d, id: d.id ?? d.id_ejecucion } as Proceso : undefined }
}

/**
 * Elimina proceso autoevaluacion · petición `DELETE`.
 *
 * @returns { ok: boolean; errors?: Record<string, string[]> }
 */
export async function eliminarProcesoService(
  id: number
): Promise<{ ok: boolean; errors?: Record<string, string[]> }> {
  const result = await http.delete(`${BASE}/${id}`)
  if (!result.ok) return { ok: false, errors: result.errors }
  return { ok: true }
}

/**
 * Consulta proceso autoevaluacion · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns any[]
 */
export async function getInstrumentosPendientesService(id_proceso: number): Promise<any[]> {
  const result = await http.get<any[]>(`${BASE}/${id_proceso}/instrumentos/pendientes`)
  return result.ok ? (result.data ?? []) : []
}

/**
 * Consulta proceso autoevaluacion · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns LiderFactor[]
 */
export async function getLideresProcesoService(id_proceso: number): Promise<LiderFactor[]> {
  const result = await http.get<LiderFactor[]>(`${BASE}/${id_proceso}/lideres`)
  return result.ok ? (result.data ?? []) : []
}

/**
 * Asigna proceso autoevaluacion · petición `POST`.
 *
 * @returns { ok: boolean; data?: LiderFactor; errors?: Record<string, string[]> }
 */
export async function asignarLiderProcesoService(
  id_proceso: number,
  payload: { id_componente: number; id_usuario: number }
): Promise<{ ok: boolean; data?: LiderFactor; errors?: Record<string, string[]> }> {
  const result = await http.post<LiderFactor>(`${BASE}/${id_proceso}/lideres`, payload)
  if (!result.ok) return { ok: false, errors: result.errors }
  return { ok: true, data: result.data }
}

/**
 * Elimina proceso autoevaluacion · petición `DELETE`.
 *
 * @returns { ok: boolean; message?: string }
 */
export async function eliminarLiderProcesoService(
  id_proceso: number,
  id_lider: number
): Promise<{ ok: boolean; message?: string }> {
  const result = await http.delete(`${BASE}/${id_proceso}/lideres/${id_lider}`)
  if (!result.ok) {
    const msg = Object.values(result.errors ?? {}).flat()[0] ?? `HTTP ${result.status}`
    return { ok: false, message: msg }
  }
  return { ok: true }
}
